/**
 * Web CheckoutViewModel Adapter
 * Platform-specific implementation for web checkout functionality
 * Integrates with existing web services and stores
 */

import { useNavigate, useLocation } from 'react-router-dom'
import { useMemo, useRef, useState, useEffect, useCallback, createElement } from 'react'
import { createCheckoutViewModel, createWebPlatformServices, WebLocationService } from '@restaurant/viewmodels'
import { useCartStore, useOrderStore, useAuthStore, useRestaurantStore, useRewardsStore, useDeliveryStore, validateOffer, getTotal, roundPriceBreakdown, transformCustomizationsToBackendFormat, normalizeCardType, toPersistedAddressId, useCloverWallets, CloverTokenizationService, isRetriableRequestError, buildFeeCalculationItems } from '@restaurant/core'
import { OrderService, POSService, CartSyncService } from '@restaurant/core'
import type { CartSyncContext, WalletTokenResult, UseCloverWalletsReturn } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'

import type { FeeCalculation } from '@restaurant/core'
import type {
  CheckoutService,
  PaymentMethodOption,
  SelectedTip,
  OrderSummaryData,
  CheckoutValidation,
  PlaceOrderResult,
} from '@restaurant/viewmodels'

function computeFormattedPickupTime(estimatedMinutes: number): string {
  const now = new Date()
  const pickupTime = new Date(now.getTime() + estimatedMinutes * 60 * 1000)
  return pickupTime.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Structured error thrown by WebCheckoutService.placeOrder() on 402 responses.
 * CheckoutViewModel discriminates payment errors from server/network errors via
 * the .type field, and uses .errorType to distinguish card_removed (triggers
 * payment method reload) from card_declined (show inline message, retry).
 */
export class PaymentDeclinedError extends Error {
  readonly type = 'payment_declined' as const
  readonly errorType: 'card_removed' | 'card_declined'
  readonly statusCode: number
  readonly details: string | undefined

  constructor(
    errorType: 'card_removed' | 'card_declined',
    details?: string,
    statusCode = 402
  ) {
    super('Payment declined')
    this.name = 'PaymentDeclinedError'
    this.errorType = errorType
    this.statusCode = statusCode
    this.details = details
  }
}

/** Shape of the JSON body returned by the backend on a failed place_order request. */
interface PlaceOrderErrorData {
  error_type?: string
  card_removed?: boolean
  details?: string
}

/**
 * Thrown by placeOrder() when no draft order exists and re-creating one at
 * checkout also fails. CheckoutViewModel discriminates it via `.type` and
 * surfaces the actionable message instead of the opaque "Order ID is required"
 * error the OrderService would otherwise throw on an empty orderId.
 */
export class CartNotSyncedError extends Error {
  readonly type = 'cart_not_synced' as const

  constructor(message = "We couldn't save your cart, please retry.") {
    super(message)
    this.name = 'CartNotSyncedError'
  }
}

/**
 * Thrown when a wallet charge amount is requested but the fee call fell back to
 * cached cart values.
 *
 * Card checkout deliberately still proceeds on estimated fees — the server
 * re-computes at placement and rejects a >$0.02 delta with an actionable error,
 * and the cached values are usually right. A wallet is different: the amount is
 * authorized by the customer BEFORE the server gets a chance to reject, so an
 * estimate must not be presented as the price.
 */
export class FeesUnavailableError extends Error {
  readonly type = 'fees_unavailable' as const

  constructor(message = "We couldn't confirm the total, please retry.") {
    super(message)
    this.name = 'FeesUnavailableError'
  }
}

/** Backoff before each calculate-fees retry. Length also caps the retry count. */
const FEE_RETRY_DELAYS_MS = [1000, 3000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Web-specific CheckoutService implementation
 * Integrates with existing web services and Zustand stores
 */
export class WebCheckoutService implements CheckoutService {
  private orderService = new OrderService({ apiClient })
  private posService = new POSService({ apiClient })
  /**
   * `${userId}:${merchantId}` pairs the backend has already answered 403
   * ("first-time customer at this merchant") for. CheckoutViewModel re-runs
   * getPaymentMethods() on every fee/summary refresh; without this memo each
   * cart mutation re-issued the reconcile call (36× in one 15-min session,
   * 2026-08-24) although the answer cannot change until a card is saved or a
   * customer is created — both of which patch `pos_customer_ids` in the auth
   * store, which is checked first and bypasses this memo.
   */
  private firstTimeCustomerKeys = new Set<string>()

  /**
   * Resolve the draft order id for checkout. If cart sync already created a
   * draft, return it. Otherwise (the cross-device sync failed earlier — e.g. a
   * transient backend 400 during login-time hydration — leaving draftOrderId
   * empty) re-attempt draft creation now from the fully-hydrated cart so the
   * user isn't permanently blocked. Throws CartNotSyncedError if a draft still
   * can't be created.
   */
  private async ensureDraftOrderId(): Promise<string> {
    const cartState = useCartStore.getState()
    const existing = cartState.draftOrderId
    if (existing) return existing

    const user = useAuthStore.getState().user
    const restaurantState = useRestaurantStore.getState()
    const location = restaurantState.currentLocation
    const restaurant = restaurantState.currentRestaurant
    const isDelivery = useOrderStore.getState().orderType === 'Delivery'
    const deliveryAddressId = isDelivery
      ? toPersistedAddressId(useDeliveryStore.getState().currentAddress?.id)
      : undefined

    // Mirror useCartSync.createSyncContext's full merchant-id resolution
    // (originalMerchantId -> merchantId -> first location -> posIntegration).
    // Do NOT fall back to cartState.restaurantId: that's the business id, and
    // sending it as clover_merchant_id would create a draft whose restaurants
    // FK can't resolve. If none resolves we throw below (the hook returns null).
    const merchantId =
      location?.originalMerchantId ||
      location?.merchantId ||
      restaurant?.locations?.[0]?.originalMerchantId ||
      restaurant?.locations?.[0]?.merchantId ||
      restaurant?.posIntegration?.merchantId

    if (!user?.id || !restaurant?.id || !merchantId || cartState.items.length === 0) {
      throw new CartNotSyncedError()
    }

    const breakdown = cartState.getPriceBreakdown()
    const context: CartSyncContext = {
      userId: user.id,
      businessId: restaurant.id,
      merchantId,
      platform: 'Web',
      // `||` (not `??`) to match the hook's createSyncContext fallback exactly.
      taxRate: location?.taxRate || restaurant?.taxRate || 0.0875,
      isDelivery,
      environment: restaurantState.isDebugMode ? 'dev' : 'prod',
      deliveryAddressId,
    }
    const priceBreakdown = {
      subtotal: breakdown.subtotal,
      deliveryFee: breakdown.deliveryFee,
      feesAndTaxes: breakdown.tax + (breakdown.serviceFee ?? 0),
      driverTip: breakdown.tip,
    }

    let newDraftId: string | null = null
    try {
      // Constructed on demand (not a field) so the rare re-attempt path is the
      // only thing that references CartSyncService — keeps the common case and
      // existing tests free of an extra dependency.
      const cartSyncService = new CartSyncService({ apiClient })
      newDraftId = await cartSyncService.syncAllItems(
        context,
        cartState.items,
        priceBreakdown
      )
    } catch (err) {
      safeGetLogger()?.error('Draft re-creation at checkout failed', {
        domain: 'order',
        error: String(err),
      })
    }

    if (!newDraftId) {
      throw new CartNotSyncedError()
    }

    cartState.setDraftOrderId(newDraftId)
    return newDraftId
  }

  /**
   * Appends Apple Pay / Google Pay options when the current location's flags
   * allow it. Called only from getPaymentMethods() success-path returns —
   * never from the !user guard or catch-all error exits, so a signed-out or
   * errored session never shows a wallet button (a wallet token minted with
   * no user burns the single-use token with no possible order).
   */
  private appendWalletMethods(methods: PaymentMethodOption[]): PaymentMethodOption[] {
    const loc = useRestaurantStore.getState().currentLocation
    if (loc?.applePayEnabled) {
      methods.push({ id: 'apple_pay', type: 'applepay', text: 'Apple Pay', selected: false, showDeleteButton: false, value: 'apple_pay' })
    }
    if (loc?.googlePayEnabled) {
      methods.push({ id: 'google_pay', type: 'googlepay', text: 'Google Pay', selected: false, showDeleteButton: false, value: 'google_pay' })
    }
    return methods
  }

  async getPaymentMethods(): Promise<PaymentMethodOption[]> {
    try {
      // Get user and restaurant info
      const user = useAuthStore.getState().user
      if (!user) {
        return []
      }

      const merchantId = useRestaurantStore.getState().currentLocation?.merchantId ?? useCartStore.getState().restaurantId ?? ''
      const isDebugMode = useRestaurantStore.getState().isDebugMode

      // Resolve Clover customer ID from user metadata.
      // pos_customer_ids is keyed by merchant ID: { merchant_id: clover_customer_id }
      let cloverCustomerId = user.metadata?.pos_customer_ids?.[merchantId]

      // If no customer ID mapping exists, attempt reconciliation (skip in debug mode)
      if (!cloverCustomerId && !isDebugMode && merchantId) {
        const firstTimeKey = `${user.id}:${merchantId}`
        if (this.firstTimeCustomerKeys.has(firstTimeKey)) {
          // Already established this session: first-time customer, no card.
          return this.appendWalletMethods([])
        }
        try {
          const result = await this.posService.reconcilePosCustomer(merchantId)
          cloverCustomerId = result.customerId

          // Patch the auth store so future calls skip reconciliation
          useAuthStore.getState().patchPosCustomerId(merchantId, result.customerId)

          // If reconciliation found a card, return it directly
          if (result.card) {
            return this.appendWalletMethods([
              {
                id: result.card.id,
                type: normalizeCardType(result.card.cardType),
                text: `Card ending in ${result.card.last4}`,
                selected: true,
                showDeleteButton: true,
                value: result.card.id,
              },
            ])
          }

          // Customer exists but no card — return empty (user sees add-card UI)
          return this.appendWalletMethods([])
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 403) {
            // First-time customer at this merchant. Expected, not a failure —
            // still eligible for wallet options (no saved card, but Apple/
            // Google Pay don't require one).
            this.firstTimeCustomerKeys.add(firstTimeKey)
            safeGetLogger()?.info('POS customer reconciliation: first-time customer at merchant', {
              domain: 'checkout',
              merchantId,
            })
            return this.appendWalletMethods([])
          }
          safeGetLogger()?.warn('POS customer reconciliation failed, continuing without card', {
            domain: 'checkout',
            error: String(err),
          })
          return []
        }
      }

      // Short-circuit: no Clover customer ID means no saved card on file
      if (!cloverCustomerId) {
        return this.appendWalletMethods([])
      }

      // Fetch saved card details from POS system using the Clover customer ID
      try {
        const cardDetails = await this.posService.getCardDetails(merchantId, cloverCustomerId)
        if (cardDetails) {
          return this.appendWalletMethods([
            {
              id: cardDetails.id,
              type: normalizeCardType(cardDetails.cardType),
              text: `Card ending in ${cardDetails.last4}`,
              selected: true,
              showDeleteButton: true,
              value: cardDetails.id,
            },
          ])
        }
        return this.appendWalletMethods([])
      } catch (err) {
        safeGetLogger()?.warn('Could not load saved card details', { domain: 'checkout', error: String(err) })
        return []
      }
    } catch (err) {
      safeGetLogger()?.error('Payment methods fetch failed', { domain: 'checkout', error: String(err) })
      return []
    }
  }

  async selectPaymentMethod(paymentMethod: PaymentMethodOption): Promise<void> {
    // Store selected payment method in session storage for checkout
    sessionStorage.setItem('selectedPaymentMethod', JSON.stringify(paymentMethod))
  }

  async deletePaymentMethod(_paymentMethod: PaymentMethodOption): Promise<void> {
    const user = useAuthStore.getState().user
    const merchantId = useRestaurantStore.getState().currentLocation?.merchantId ?? useCartStore.getState().restaurantId

    if (merchantId) {
      const cloverCustomerId = (user?.metadata?.pos_customer_ids as Record<string, string> | undefined)?.[merchantId]
      if (cloverCustomerId) {
        await this.posService.removeCard(merchantId, cloverCustomerId)
      }
    }
  }

  calculateTip(percentage: number, type: 'preset' | 'custom'): SelectedTip {
    const cartState = useCartStore.getState()
    const subtotal = cartState.subtotal || 0
    const amount = (subtotal * percentage) / 100

    // Update cart store with tip
    cartState.setTip(amount)

    return {
      percentage,
      amount,
      selectedType: type,
    }
  }

  setCustomTip(amount: string): void {
    const numericAmount = parseFloat(amount)
    if (!isNaN(numericAmount) && numericAmount >= 0) {
      const cartState = useCartStore.getState()
      cartState.setTip(numericAmount)
    }
  }

  async getOrderSummary(): Promise<OrderSummaryData> {
    const cartState = useCartStore.getState()
    const restaurantState = useRestaurantStore.getState()
    const isDelivery = useOrderStore.getState().orderType === 'Delivery'

    // Clear any stale tip for pickup orders (no driver to tip)
    if (!isDelivery && cartState.tip > 0) {
      cartState.setTip(0)
    }

    const restaurantName = restaurantState.currentLocation?.name ?? 'Restaurant'
    const restaurantAddress = [
      restaurantState.currentLocation?.address,
      restaurantState.currentLocation?.city,
    ].filter(Boolean).join(', ')

    const lineItems: Array<{ label: string; amount: string }> = [
      {
        label: 'Subtotal',
        amount: `$${cartState.subtotal.toFixed(2)}`,
      },
    ]

    if (isDelivery && cartState.tip > 0) {
      lineItems.push({
        label: 'Driver Tip',
        amount: `$${cartState.tip.toFixed(2)}`,
      })
    }

    if (isDelivery && cartState.deliveryFee > 0) {
      lineItems.push({
        label: 'Delivery Fee',
        amount: `$${cartState.deliveryFee.toFixed(2)}`,
      })
    }

    // Use the same authoritative fee math as placeOrder/getWalletChargeAmount
    // (fresh backend calculate-fees, with cached-cart-store fallback inside
    // computeOrderTotals) so the displayed taxes/total always match the amount
    // actually charged. The cached cart-store math could drift by a cent from
    // the backend (2026-07-17: sheet showed $3.62, wallet charged $3.61).
    const { feesAndTaxes: taxesAndFees } = await this.computeOrderTotals()

    if (taxesAndFees > 0) {
      lineItems.push({
        label: 'Taxes and Fees',
        amount: `$${taxesAndFees.toFixed(2)}`,
      })
    }

    if (cartState.offerDiscount > 0) {
      lineItems.push({
        label: 'Offer Discount',
        amount: `-$${cartState.offerDiscount.toFixed(2)}`,
      })
    }

    const rewardsState = useRewardsStore.getState()
    const rewardDiscount = rewardsState.calculateRewardDiscount(cartState.subtotal)

    if (rewardDiscount > 0) {
      lineItems.push({
        label: 'Reward Discount',
        amount: `-$${rewardDiscount.toFixed(2)}`,
      })
    }

    // Compute total from displayed values (backend fees, not cartStore.total which uses local tax)
    const displayTotal = cartState.subtotal
      + taxesAndFees
      + (isDelivery ? cartState.deliveryFee : 0)
      + (isDelivery ? cartState.tip : 0)
      - cartState.offerDiscount
      - rewardDiscount
    const adjustedTotal = Math.max(displayTotal, 0)

    return {
      title: `${isDelivery ? 'Delivery' : 'Pickup'} from ${restaurantName}`,
      locationAddress: restaurantAddress,
      lineItems,
      total: `$${adjustedTotal.toFixed(2)}`,
    }
  }

  async validateCheckout(): Promise<CheckoutValidation> {
    const errors: string[] = []
    
    // Check if cart has items
    const cartState = useCartStore.getState()
    if (cartState.isCartEmpty()) {
      errors.push('Cart is empty')
    }

    // Check if user is authenticated
    const user = useAuthStore.getState().user
    if (!user) {
      errors.push('User not authenticated')
    }

    // Check if payment method is selected
    const selectedPaymentMethod = sessionStorage.getItem('selectedPaymentMethod')
    if (!selectedPaymentMethod) {
      errors.push('No payment method selected')
    }

    // Check if restaurant is selected
    if (!cartState.restaurantId) {
      errors.push('No restaurant selected')
    }

    // Validate applied offer is still valid
    if (cartState.appliedOfferId) {
      const offer = useRewardsStore.getState().availableOffers.find(
        (o) => o.id === cartState.appliedOfferId
      )
      if (offer) {
        const result = validateOffer(offer, {
          subtotal: cartState.subtotal,
          cartItems: cartState.items,
          currentStoreId: useRestaurantStore.getState().currentLocation?.id || cartState.restaurantId || '',
          appliedOfferId: null, // Don't fail on self-check
          appliedRewardId: null,
        })
        if (result.status !== 'valid') {
          errors.push(result.message || 'Applied offer is no longer valid.')
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Authoritative totals for order placement. Falls back to local cart values
   * when the fee call fails.
   *
   * `feesAreEstimated` reports whether that fallback was used. The returned
   * object is otherwise indistinguishable from a real server result, and callers
   * that authorize money up front (wallets) must not treat an estimate as the
   * price. The cached values come from a prior calculate-fees on a possibly
   * different cart, and `fetchServiceFee` posts WITHOUT the delivery address,
   * so they are an approximation even when present.
   */
  private async computeOrderTotals(): Promise<{
    feesAndTaxes: number
    orderTotal: number
    feesAreEstimated: boolean
  }> {
    const cartState = useCartStore.getState()
    const restaurantState = useRestaurantStore.getState()
    const location = restaurantState.currentLocation
    const isDelivery = useOrderStore.getState().orderType === 'Delivery'
    const currentAddress = isDelivery ? useDeliveryStore.getState().currentAddress : null

    // Fallback mirrors the display math: cached backend tax (or local estimate)
    // PLUS the cached service fee — a tax-only fallback silently dropped the
    // service fee from the charge when the fee call failed.
    let feesAndTaxes = (cartState.backendTax ?? cartState.tax ?? 0) + (cartState.serviceFee ?? 0)
    let orderTotal = cartState.total

    // A cart the backend's schema rejects 400s identically on every attempt, and
    // a 400 never retries — skip the doomed round trip and record WHICH rule the
    // cart broke, which the generic catch below cannot see.
    const { items: feeItems, invalidReason } = buildFeeCalculationItems(cartState.items)
    if (feeItems === null) {
      // empty-cart is a benign UI-timing state — totals recomputed before items
      // hydrate, or right after a post-order cart clear — not an error. A
      // genuinely malformed cart (missing id / bad quantity / bad price) still
      // logs at error, since it means the customer is charged estimated fees.
      // Keep the payload inline on the optional-chain call so it stays lazy —
      // cartState.items.length is only read when a logger is actually present.
      const logMsg = 'Fee calculation skipped: invalid cart payload, using local values'
      const logger = safeGetLogger()
      if (invalidReason === 'empty-cart') {
        logger?.debug(logMsg, { domain: 'checkout', reason: invalidReason, itemCount: cartState.items.length })
      } else {
        logger?.error(logMsg, { domain: 'checkout', reason: invalidReason, itemCount: cartState.items.length })
      }
      return { feesAndTaxes, orderTotal, feesAreEstimated: true }
    }

    for (let attempt = 0; ; attempt++) {
      try {
        const feeResult = await apiClient.post<{ fees: FeeCalculation }>(
          '/orders/calculate-fees',
          {
            restaurantId: location?.id ?? cartState.restaurantId ?? '',
            items: feeItems,
            ...(isDelivery && currentAddress && {
              deliveryAddress: {
                street: currentAddress.street,
                city: currentAddress.city,
                state: currentAddress.state,
                zipCode: currentAddress.zipCode,
              },
            }),
          }
        )
        feesAndTaxes = feeResult.fees.tax + feeResult.fees.serviceFee
        orderTotal = getTotal({
          subtotal: cartState.subtotal,
          deliveryFee: cartState.deliveryFee,
          feesAndTaxes,
          driverTip: cartState.tip,
          offerDiscount: cartState.offerDiscount ?? 0,
        })
        return { feesAndTaxes, orderTotal, feesAreEstimated: false }
      } catch (err) {
        // Undefined once `attempt` runs past the end of the table — the retry
        // budget expiring. 4xx never retries: a validation failure on this cart
        // fails identically every time.
        const retryDelayMs = FEE_RETRY_DELAYS_MS[attempt]
        if (retryDelayMs !== undefined && isRetriableRequestError(err)) {
          safeGetLogger()?.warn('Fee calculation failed, retrying', {
            domain: 'checkout', error: String(err), attempt: attempt + 1,
          })
          await sleep(retryDelayMs)
          continue
        }

        safeGetLogger()?.error('Fee calculation failed at placeOrder, using local values', {
          domain: 'checkout', error: String(err), attempts: attempt + 1,
        })
        return { feesAndTaxes, orderTotal, feesAreEstimated: true }
      }
    }
  }

  async placeOrder(): Promise<PlaceOrderResult> {
    const cartState = useCartStore.getState()
    const user = useAuthStore.getState().user

    if (!user) {
      throw new Error('User not authenticated')
    }

    if (!cartState.restaurantId) {
      throw new Error('No restaurant selected')
    }

    // Get selected payment method
    const selectedPaymentMethodJson = sessionStorage.getItem('selectedPaymentMethod')
    if (!selectedPaymentMethodJson) {
      throw new Error('No payment method selected')
    }

    const selectedPaymentMethod = JSON.parse(selectedPaymentMethodJson)

    return this.submitOrder({
      paymentMethodId: selectedPaymentMethod.id === 'saved-card' ? 'saved-card' : undefined,
      paymentToken: selectedPaymentMethod.id !== 'saved-card' ? selectedPaymentMethod.id : undefined,
    })
  }

  /**
   * Wallet orders only (web): place the order charging the given single-use
   * wallet token. Skips the sessionStorage selectedPaymentMethod read entirely
   * — the wallet sheet (Task 8) never writes it.
   */
  async placeOrderWithWalletToken(
    token: string,
    method: 'apple_pay' | 'google_pay',
    authorizedAmountCents: number,
  ): Promise<PlaceOrderResult> {
    const cartState = useCartStore.getState()
    const user = useAuthStore.getState().user

    if (!user) {
      throw new Error('User not authenticated')
    }

    if (!cartState.restaurantId) {
      throw new Error('No restaurant selected')
    }

    return this.submitOrder({
      paymentToken: token,
      paymentMethod: method,
      authorizedAmount: authorizedAmountCents,
    })
  }

  /**
   * Shared order-submission body for placeOrder() and placeOrderWithWalletToken().
   * Handles draft resolution, fee computation, delivery validation, redeemed-points
   * sync, order placement, order-store updates, and cart clear. The only difference
   * between callers is how `paymentData` (beyond amount/tipAmount, added here) is built.
   */
  private async submitOrder(paymentData: {
    paymentMethodId?: string
    paymentToken?: string
    paymentMethod?: 'apple_pay' | 'google_pay'
    authorizedAmount?: number
  }): Promise<PlaceOrderResult> {
    try {
      const cartState = useCartStore.getState()
      const restaurantState = useRestaurantStore.getState()
      // Auth is already guarded in both public entry points (placeOrder,
      // placeOrderWithWalletToken) before submitOrder is ever called.
      const user = useAuthStore.getState().user!

      // Source merchant_id from the current location's Clover merchant ID
      const merchantId = restaurantState.currentLocation?.merchantId ?? cartState.restaurantId ?? ''

      // Source order_id from the draft order created during cart sync. If the
      // draft is missing (sync failed earlier), re-attempt creation now rather
      // than placing the order with an empty id (which throws the opaque
      // "Order ID is required"). Throws CartNotSyncedError if it still can't.
      const orderId = await this.ensureDraftOrderId()

      // Resolve clover_customer_ids from user metadata keyed by merchant
      const cloverCustomerIds = (user.metadata?.pos_customer_ids ?? {}) as Record<string, string>

      // Source restaurant data from the restaurant store
      const restaurant = restaurantState.currentRestaurant
      const location = restaurantState.currentLocation

      const isDelivery = useOrderStore.getState().orderType === 'Delivery'
      const currentAddress = isDelivery ? useDeliveryStore.getState().currentAddress : null

      // Validate delivery availability before submitting the order
      if (isDelivery) {
        const currentLocationId = location?.id
        const fee = currentAddress?.storeDeliveryFees?.[currentLocationId ?? '']

        if (fee === undefined || fee === -1) {
          throw new Error('Delivery is not available for your current address and location')
        }
      }

      // Fetch authoritative fees from backend for order placement
      const { feesAndTaxes, orderTotal } = await this.computeOrderTotals()

      // Prepare order data using the new PlaceOrderData contract
      const placeOrderData = {
        merchantId,
        orderId,
        orderData: {
          id: orderId,
          items: cartState.items.map(item => {
            const isFree = (cartState.freeItemIds ?? []).includes(item.id)
            return {
              itemId: item.id,
              menuItem: {
                id: item.menuItem.id,
                cloverId: item.menuItem.cloverId ?? '',
                name: item.menuItem.name,
                price: item.menuItem.price,
                priceVariants: item.menuItem.priceVariants ?? {},
                description: item.menuItem.description ?? '',
              },
              quantity: item.quantity,
              selectedOptions: transformCustomizationsToBackendFormat(
                item.customizations,
                item.menuItem
              ),
              specialInstructions: item.specialInstructions,
              selectedPriceVariant: item.selectedPriceVariant ?? 'base',
              isFreeItem: isFree,
              offerIdSource: isFree ? cartState.appliedOfferId || undefined : undefined,
              isRewardRedemption: item.isRewardRedemption ?? false,
              rewardPointsCost: item.rewardPointsCost ?? null,
            }
          }),
          priceBreakdown: {
            ...roundPriceBreakdown({
              subtotal: cartState.subtotal,
              feesAndTaxes: feesAndTaxes,
              deliveryFee: cartState.deliveryFee,
              driverTip: cartState.tip,
              offerDiscount: cartState.offerDiscount ?? 0,
            }),
            total: orderTotal,
          },
          isDelivery,
          platform: 'Web',
          merchantId: restaurantState.currentLocation?.originalMerchantId || merchantId,
          redeemedPoints: useRewardsStore.getState().getRedemptionPayload().redeemed_points,
          appliedOfferId: cartState.appliedOfferId ?? undefined,
          ...(isDelivery && currentAddress && {
            deliveryAddress: {
              street: currentAddress.street,
              city: currentAddress.city,
              state: currentAddress.state,
              zipCode: currentAddress.zipCode,
              latitude: currentAddress.latitude ?? 0,
              longitude: currentAddress.longitude ?? 0,
              apartmentSuite: currentAddress.apartment ?? '',
              entryCode: currentAddress.entryCode ?? '',
              buildingName: currentAddress.buildingName ?? '',
              deliveryDetails: {
                dropOffOption: currentAddress.deliveryDetails?.dropOffOption === 'leaveItAtMyDoor'
                  ? 'DropOffOption.leaveItAtMyDoor'
                  : 'DropOffOption.handItToMe',
                instructions: currentAddress.deliveryDetails?.instructions ?? '',
              },
            },
          }),
        },
        restaurantData: {
          name: restaurant?.name ?? 'Restaurant',
          address: {
            street: location?.address ?? '',
            city: location?.city ?? '',
            state: location?.state ?? '',
            zipCode: location?.zipCode ?? '',
            latitude: location?.latitude ?? 0,
            longitude: location?.longitude ?? 0,
            timezone: location?.timezone ?? 'America/Los_Angeles',
          },
          phoneNumber: location?.phone ?? '',
          expectedPickupTime: location?.estimatedPrepTime ?? 30,
          taxRate: location?.taxRate ?? restaurant?.taxRate ?? 0.0875,
          businessId: restaurant?.id ?? '',
          storeId: location?.id ?? '',
          instructions: location?.instructions ?? null,
          formattedPickupTime: computeFormattedPickupTime(
            location?.estimatedPrepTime ?? 30
          ),
        },
        userData: {
          id: user.id,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phoneNumber: user.phoneNumber || undefined,
          cloverCustomerIds,
        },
        paymentData: {
          ...paymentData,
          amount: cartState.total,
          tipAmount: cartState.tip,
        },
      }

      // Sync redeemed points to the draft order before placing (mirrors Flutter's updateRedeemedPoints)
      const redeemedPoints = useRewardsStore.getState().getRedemptionPayload().redeemed_points
      if (redeemedPoints > 0 && orderId) {
        await this.orderService.updateRedeemedPoints(orderId, redeemedPoints)
      }

      // Place the order
      const order = await this.orderService.placeOrder(placeOrderData)

      // Backend response may not include pickup/delivery time estimates.
      // Enrich the order with the values we computed for the request so the
      // tracking sheet can display them immediately.
      if (!order.pickupTimeEstimated && placeOrderData.restaurantData.formattedPickupTime) {
        order.pickupTimeEstimated = placeOrderData.restaurantData.formattedPickupTime
      }

      // Update order store
      const orderState = useOrderStore.getState()
      orderState.setCurrentOrder(order)
      orderState.addToHistory(order)

      // Clear cart after successful order — skip draft deletion since the order
      // has been placed and paid, it's no longer a draft.
      cartState.clearCart({ skipDraftDeletion: true })

      // Clear selected payment method
      sessionStorage.removeItem('selectedPaymentMethod')

      // Clear reward redemption state and refetch updated balance
      const rewardsState = useRewardsStore.getState()
      rewardsState.clearAppliedItems()

      // Refetch balance from backend so UI shows updated points
      const merchantIds = rewardsState.merchantConfig?.merchant_id || []
      const balanceMerchantId = merchantIds[0]
      if (balanceMerchantId && user?.id) {
        apiClient.get<{ current_points?: number; currentPoints?: number }>(
          `/customer_rewards/${balanceMerchantId}/user/${user.id}`
        ).then((res) => {
          const newBalance = res.current_points ?? res.currentPoints ?? 0
          useRewardsStore.getState().setBalance(newBalance)
        }).catch(() => {
          // Non-critical — balance will refresh on next page load
        })
      }

      return {
        id: order.id,
        orderNumber: order.externalId || order.id.slice(-6).toUpperCase(),
        status: order.status,
        total: getTotal(order.priceBreakdown),
        type: isDelivery ? 'delivery' : 'pickup',
        order,
      }
    } catch (err: unknown) {
      safeGetLogger()?.error('Order placement failed', { domain: 'order', error: String(err) })

      const errResponse = (err as { response?: { status?: number; data?: PlaceOrderErrorData } } | null | undefined)?.response
      const status = errResponse?.status
      const responseData = errResponse?.data

      if (status === 402) {
        // Map 402 responses to a typed PaymentDeclinedError so CheckoutViewModel
        // can handle card_removed (reload payment list) vs card_declined (retry)
        // distinctly from server/network failures.
        const errorType: 'card_removed' | 'card_declined' =
          responseData?.error_type === 'card_removed' || responseData?.card_removed === true
            ? 'card_removed'
            : 'card_declined'
        if (errorType === 'card_removed') {
          // The backend deleted the saved card — the persisted selection must
          // die with it, or a later placeOrder() reuses a card that no longer
          // exists and 402s with card_on_file_missing (2026-08-11 retry loop).
          sessionStorage.removeItem('selectedPaymentMethod')
        }
        throw new PaymentDeclinedError(errorType, responseData?.details, 402)
      }

      // For 500 and network errors: rethrow the original error so that the
      // ApiClient-set .type field ('serverError' | 'network') reaches
      // CheckoutViewModel unchanged, enabling the generic toast path.
      throw err
    }
  }

  /**
   * Wallet orders only (web): cents total for the wallet sheet, computed with
   * the same authoritative fee math placeOrder uses.
   */
  async getWalletChargeAmount(): Promise<number> {
    const { orderTotal, feesAreEstimated } = await this.computeOrderTotals()
    // The customer authorizes this amount before the server can reconcile it,
    // so an estimate must never be presented as the price. Callers leave the
    // wallet button unmounted rather than quoting a number we can't stand behind.
    if (feesAreEstimated) throw new FeesUnavailableError()
    return Math.round(orderTotal * 100)
  }

  checkLocationAcceptingOrders(): { available: boolean; hoursText: string } {
    const location = useRestaurantStore.getState().currentLocation
    if (!location) {
      return { available: false, hoursText: 'No location selected' }
    }
    const isDebugMode = useRestaurantStore.getState().isDebugMode
    const locationService = new WebLocationService(null, isDebugMode)
    return {
      available: locationService.checkLocationAvailability(location),
      hoursText: locationService.formatHoursText(location),
    }
  }
}

/**
 * Options accepted by useCheckoutViewModel.
 */
export interface UseCheckoutViewModelOptions {
  /**
   * Called after a successful place_order response.
   * CartPage uses this to transition the sheet to order-tracking mode.
   * The full Order is available via result.order.
   */
  onOrderPlaced?: (result: import('@restaurant/viewmodels').PlaceOrderResult) => void
  /** Called when "Add Card" or "Update Card" is pressed in the checkout sheet. */
  onAddCardPress?: () => void
  /** Called when user taps "Change Location" on closed-location alert. */
  onChangeLocation?: () => void
}

/**
 * Web CheckoutViewModel hook
 * Uses React Router and web-specific platform services
 */
export const useCheckoutViewModel = (options?: UseCheckoutViewModelOptions) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Stabilize the callback refs so they don't trigger re-memoization
  const onOrderPlacedRef = useRef(options?.onOrderPlaced)
  onOrderPlacedRef.current = options?.onOrderPlaced
  const onAddCardPressRef = useRef(options?.onAddCardPress)
  onAddCardPressRef.current = options?.onAddCardPress
  const onChangeLocationRef = useRef(options?.onChangeLocation)
  onChangeLocationRef.current = options?.onChangeLocation

  // Memoize services and the ViewModel factory to avoid infinite re-render loops.
  // createCheckoutViewModel's useEffect depends on [checkoutService], so a new
  // instance on every render would re-trigger initialization endlessly.
  const platformServices = useMemo(() => createWebPlatformServices(navigate, location), [navigate, location])
  const checkoutService = useMemo(() => new WebCheckoutService(), [])

  const CheckoutViewModelHook = useMemo(
    () => createCheckoutViewModel(platformServices, {
      checkoutService,
      onOrderPlaced: (...args) => onOrderPlacedRef.current?.(...args),
      onAddCardPress: () => onAddCardPressRef.current?.(),
      onChangeLocation: () => onChangeLocationRef.current?.(),
    }),
    [platformServices, checkoutService],
  )

  const vm = CheckoutViewModelHook()

  // ── Wallet support (web only) ────────────────────────────────────────────
  // The shared CheckoutViewModel only knows the CheckoutService interface and
  // is consumed by the native app too, so all Apple Pay / Google Pay
  // orchestration lives here in the web-only wrapper. `checkoutService` is
  // the concrete WebCheckoutService, so the optional wallet methods on
  // CheckoutService (placeOrderWithWalletToken / getWalletChargeAmount) are
  // guaranteed present.
  const WALLET_BUTTON_CONTAINER_ID = 'wallet-button-container'
  const WALLET_ERROR_MESSAGE =
    'Payment could not be completed. You have not been charged — please try again.'

  const { user } = useAuthStore()
  const merchantId = useRestaurantStore.getState().currentLocation?.merchantId ?? ''

  // PAKMS acquisition — same pattern as useCloverCardAdd.ts
  const tokenizationService = useMemo(
    () => new CloverTokenizationService({ apiClient, userId: user?.id }),
    [user?.id],
  )
  const [pakmsKey, setPakmsKey] = useState<string | null>(null)
  useEffect(() => {
    if (!merchantId) return
    let cancelled = false
    tokenizationService.fetchPakmsKey(merchantId)
      .then((key: string) => { if (!cancelled) setPakmsKey(key) })
      .catch(() => { if (!cancelled) setPakmsKey(null) }) // wallet buttons simply stay unavailable
    return () => { cancelled = true }
  }, [tokenizationService, merchantId])

  const [walletError, setWalletError] = useState<string | null>(null)
  const [walletPlacing, setWalletPlacing] = useState(false)
  const walletPlacingRef = useRef(false)
  const lastAuthorizedAmountRef = useRef(0)

  const selectedId = vm.selectedPaymentMethodId
  const walletMethod =
    selectedId === 'apple_pay' || selectedId === 'google_pay'
      ? (selectedId as 'apple_pay' | 'google_pay')
      : null

  // `onWalletToken` (passed into useCloverWallets below as the `onToken`
  // option) needs to call back into `wallets` and `remountWalletButton` —
  // both of which are only produced *from* that same useCloverWallets call,
  // a genuine circular dependency. Resolve it with refs, assigned fresh each
  // render, the same pattern the file already uses for onOrderPlacedRef etc.
  const walletsRef = useRef<UseCloverWalletsReturn | null>(null)
  const remountWalletButtonRef = useRef<() => Promise<void>>(async () => {})

  const onWalletToken = useCallback(async ({ token, method }: WalletTokenResult) => {
    // Re-entrancy guard: ignore a second token event while an order is
    // already in flight (e.g. a duplicate wallet-sheet callback).
    if (walletPlacingRef.current) return
    walletPlacingRef.current = true
    setWalletPlacing(true)
    setWalletError(null)
    try {
      // Concrete WebCheckoutService — optional methods are implemented (Task 7)
      const result = await checkoutService.placeOrderWithWalletToken!(
        token, method, lastAuthorizedAmountRef.current)
      // Apple session status reflects the REAL charge result, reported AFTER
      // the charge promise resolves.
      if (method === 'apple_pay') walletsRef.current?.reportApplePaymentResult('success')
      onOrderPlacedRef.current?.(result)
    } catch {
      // Charge declined OR pre-charge 400 (stale cart / amount changed):
      // sheet shows failed; token is single-use — full element reset + remount.
      if (method === 'apple_pay') walletsRef.current?.reportApplePaymentResult('failed')
      walletsRef.current?.reset()
      void remountWalletButtonRef.current()
      setWalletError(WALLET_ERROR_MESSAGE) // never the saved-card-removed copy
    } finally {
      walletPlacingRef.current = false
      setWalletPlacing(false)
    }
  }, [checkoutService])

  const onWalletCancel = useCallback(() => {
    // Sheet dismissed without a token — back to idle, no error.
    setWalletPlacing(false)
    setWalletError(null)
  }, [])

  const wallets = useCloverWallets({ pakmsKey, onToken: onWalletToken, onCancel: onWalletCancel })
  walletsRef.current = wallets

  const remountWalletButton = useCallback(async () => {
    if (!walletMethod) return
    try {
      const amountCents = await checkoutService.getWalletChargeAmount!()
      lastAuthorizedAmountRef.current = amountCents
      if (walletMethod === 'apple_pay') wallets.mountAppleButton(`#${WALLET_BUTTON_CONTAINER_ID}`, amountCents)
      else wallets.mountGoogleButton(`#${WALLET_BUTTON_CONTAINER_ID}`, amountCents)
    } catch (err) {
      // Without this, a rejected charge-amount fetch is a silent dead button
      // (the ref-callback caller void's this promise).
      safeGetLogger()?.error('Wallet button remount failed', {
        domain: 'checkout', method: walletMethod, error: String(err),
      })
    }
  }, [walletMethod, wallets, checkoutService])
  remountWalletButtonRef.current = remountWalletButton

  const walletContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) void remountWalletButtonRef.current()
  }, [])

  useEffect(() => {
    // Mounting is handled by the customButton container's ref callback below
    // (it fires whenever the div (re)enters the DOM, e.g. after the checkout
    // sheet is closed and reopened). This effect only handles deselect
    // cleanup.
    if (!walletMethod) {
      wallets.reset()
      // Clear any stale wallet-decline error so switching to a saved card
      // doesn't leave the previous wallet's error message rendered.
      setWalletError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletMethod])

  // Tip changes re-reconcile the sheet amount (Apple: update; Google: re-create)
  const tipSignature = JSON.stringify(vm.selectedTip ?? null) + (vm.customTipAmount ?? '')
  useEffect(() => {
    if (!walletMethod) return
    void checkoutService.getWalletChargeAmount!().then((cents) => {
      lastAuthorizedAmountRef.current = cents
      wallets.updateAmount(cents)
    }).catch((err) => {
      // Rejects when fees fell back to estimates (FeesUnavailableError). Leave
      // the previously authorized amount in place rather than quoting an
      // estimate; without this the rejection is unhandled.
      safeGetLogger()?.error('Wallet amount update failed', {
        domain: 'checkout', method: walletMethod, error: String(err),
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipSignature])

  const filteredPaymentMethods = vm.paymentMethods.filter((m: PaymentMethodOption) =>
    (m.id !== 'apple_pay' || wallets.appleAvailable) &&
    (m.id !== 'google_pay' || wallets.googleAvailable))

  return {
    ...vm,
    paymentMethods: filteredPaymentMethods,
    paymentMethodProps: {
      ...vm.paymentMethodProps,
      paymentMethods: filteredPaymentMethods,
    },
    orderSummaryProps: {
      ...vm.orderSummaryProps,
      ...(walletError && { errorMessage: walletError }),
      loading: vm.orderSummaryProps.loading || walletPlacing,
      customButton: walletMethod
        ? createElement('div', {
            id: WALLET_BUTTON_CONTAINER_ID,
            // Keyed by walletMethod so switching apple_pay <-> google_pay
            // directly (no deselect in between) forces React to unmount the
            // old div and mount a fresh one — otherwise the same DOM node
            // (same id) is reused, the ref callback never re-fires, and the
            // previous wallet's button stays mounted.
            key: walletMethod,
            // Mount whenever this div (re)enters the DOM — covers the
            // initial mount AND the checkout sheet being closed/reopened
            // with a wallet method still selected (SheetWeb unmounts
            // children on close, so the walletMethod-only effect above
            // never re-fires and would otherwise leave a dead button).
            ref: walletContainerRef,
            // Clover minimums: Apple ≥200×32 (default 300×40), Google ≥90×40
            style: { width: '100%', minHeight: 40 },
            'data-testid': 'wallet-button-container',
          })
        : undefined,
    },
  }
}